
import React from 'react';
import { Award } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navigation from '@/components/layout/Navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { activityService } from '@/services/ActivityService';
import { useTotalXP } from '@/hooks/useTotalXP';
import ProfileHeader from '@/components/profile/ProfileHeader';
import AchievementsTab from '@/components/profile/AchievementsTab';
import ProgressTab from '@/components/profile/ProgressTab';
import ActivityTab from '@/components/profile/ActivityTab';

const Profile = () => {
  const { user, logout } = useAuth();
  const profile = user?.profile;
  const { totalXP, isLoading: isLoadingXP } = useTotalXP();
  
  const { data: activityLogs = [], isLoading: isLoadingActivities } = useQuery({
    queryKey: ['activities', user?.id],
    queryFn: () => user ? activityService.getUserActivities(user.id) : Promise.resolve([]),
    enabled: !!user,
  });
  
  const streak = profile?.streak || 0;
  const wordsLearned = profile?.wordsLearned || 0;
  const level = profile?.level || 1;
  const displayName = user?.name || 'User';
  
  const nextLevelXp = level * 300;
  const xpProgress = Math.min(((totalXP % nextLevelXp) / nextLevelXp) * 100, 100);
  
  const handleLogout = () => {
    logout();
  };
  
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container max-w-md mx-auto px-4 py-6">
        <Card className="bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-none mb-6">
          <CardContent className="pt-6">
            <ProfileHeader
              user={user}
              profile={profile}
              streak={streak}
              wordsLearned={wordsLearned}
              level={level}
              displayName={displayName}
              totalXP={totalXP}
              isLoadingXP={isLoadingXP}
              handleLogout={handleLogout}
            />
          </CardContent>
        </Card>
        
        <Tabs defaultValue="achievements" className="mb-8">
          <TabsList className="w-full">
            <TabsTrigger value="achievements" className="flex-1">Achievements</TabsTrigger>
            <TabsTrigger value="progress" className="flex-1">Progress</TabsTrigger>
            <TabsTrigger value="activity" className="flex-1">Activity</TabsTrigger>
          </TabsList>
          
          <TabsContent value="achievements" className="mt-4 space-y-4 animate-fade-in">
            <AchievementsTab
              streak={streak}
              wordsLearned={wordsLearned}
              totalXP={totalXP}
            />
          </TabsContent>
          
          <TabsContent value="progress" className="mt-4 animate-fade-in">
            <ProgressTab
              level={level}
              xpProgress={xpProgress}
              totalXP={totalXP}
            />
          </TabsContent>
          
          <TabsContent value="activity" className="mt-4 animate-fade-in">
            <ActivityTab 
              activityLogs={activityLogs}
              isLoadingActivities={isLoadingActivities}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      <Navigation />
    </div>
  );
};

export default Profile;
